import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { InterventionLadderStepModel } from '../../../api-service/clients';
import { formatNumber } from '../../../utilities';
import { externalCostColor, internalCostColor, stepBackgrounds } from './intervention-ladder-colors';

const million = 1_000_000;

const activeSectionColor = stepBackgrounds[stepBackgrounds.length - 1];

const activeSectionBackground = 'rgba(24, 76, 97, 0.1)';

export function formatMillions(value: number) {
  if (value === 0) {
    return '0';
  }
  // A small cost must not read as nothing.
  return value < million / 20 ? '< 0,1' : formatNumber(value / million, 1);
}

export function formatActivitiesWithoutPrice(count: number) {
  return `${formatNumber(count)} ${count === 1 ? 'aktivitet' : 'aktiviteter'} uden pris`;
}

function formatShareOfTotal(value: number, total: number) {
  const share = (value / total) * 100;
  return `${share > 0 && share < 0.5 ? '< 1' : formatNumber(share)} % af total`;
}

interface InterventionLadderStepCostProps {
  step: InterventionLadderStepModel;
  totalCost: number;
  isSelected: boolean;
  onSelect: () => void;
}

export function InterventionLadderStepCost({ step, totalCost, isSelected, onSelect }: InterventionLadderStepCostProps) {
  const { cost } = step;
  return (
    <InterventionLadderStepSection
      heading="Udgift"
      value={formatMillions(cost.total)}
      unit="mio. kr."
      isSelected={isSelected}
      onSelect={onSelect}
      ariaLabel={`Udgifter på trin ${step.step}: ${formatMillions(cost.total)} mio. kr.`}
    >
      <InternalExternalSplit
        internal={cost.internal}
        external={cost.external}
        format={formatMillions}
        label={`Intern ${formatMillions(cost.internal)} mio. kr., ekstern ${formatMillions(cost.external)} mio. kr.`}
        aside={totalCost > 0 ? formatShareOfTotal(cost.total, totalCost) : undefined}
      />
    </InterventionLadderStepSection>
  );
}

interface InterventionLadderStepSectionProps {
  heading: string;
  value: string;
  unit: string;
  isSelected: boolean;
  onSelect: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}

export function InterventionLadderStepSection({ heading, value, unit, isSelected, onSelect, ariaLabel, children }: InterventionLadderStepSectionProps) {
  return (
    <ButtonBase
      component="div"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={ariaLabel}
      sx={{
        display: 'block',
        textAlign: 'left',
        font: 'inherit',
        px: 1.5,
        py: 1.25,
        color: '#16323d',
        bgcolor: isSelected ? activeSectionBackground : 'transparent',
        boxShadow: isSelected ? `inset 4px 0 0 ${activeSectionColor}` : 'none',
        '&:hover': { bgcolor: isSelected ? activeSectionBackground : 'rgba(24, 76, 97, 0.04)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Box
          sx={{
            fontSize: '0.6875rem',
            fontWeight: isSelected ? 700 : 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: isSelected ? activeSectionColor : '#4c5f6a',
          }}
        >
          {heading}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Box sx={{ fontSize: '1.125rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
          <Box sx={{ fontSize: '0.6875rem' }}>{unit}</Box>
        </Box>
      </Box>
      {children}
    </ButtonBase>
  );
}

interface InternalExternalSplitProps {
  internal: number;
  external: number;
  format: (value: number) => string;
  label: string;
  aside?: string;
}

export function InternalExternalSplit({ internal, external, format, label, aside }: InternalExternalSplitProps) {
  const total = internal + external;
  function shareOf(value: number) {
    return total > 0 ? (value / total) * 100 : 0;
  }

  return (
    <Box>
      <Box role="img" aria-label={label} sx={{ display: 'flex', height: 6, mt: 0.75, borderRadius: '3px', overflow: 'hidden', bgcolor: 'rgba(24, 76, 97, 0.1)' }}>
        <Box sx={{ width: `${shareOf(internal)}%`, bgcolor: internalCostColor }} />
        <Box sx={{ width: `${shareOf(external)}%`, bgcolor: externalCostColor }} />
      </Box>
      <Box aria-hidden sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 0.75, fontSize: '0.6875rem', color: '#16323d', fontVariantNumeric: 'tabular-nums' }}>
        <span>
          Intern {format(internal)} · Ekstern {format(external)}
        </span>
        {aside && (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {aside}
          </Box>
        )}
      </Box>
    </Box>
  );
}
